/**
 * A file to store 'lists' that are used to show things in the car hire page
 */
import * as React from 'react'
import type { IntlShape } from 'react-intl'
import LineFileAltIcon from 'LuxKit/Icons/line/LineFileAltIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import LineClockEightIcon from 'LuxKit/Icons/line/LineClockEightIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineFileTimesAltIcon from 'LuxKit/Icons/line/LineFileTimesAltIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineShieldPlusIcon from 'LuxKit/Icons/line/LineShieldPlusIcon'
import { SUPPORT_DEFAULT_PHONE_DISPLAY } from 'constants/support'

interface FAQ {
  icon: React.ReactElement
  question: string
  answer: string
}

export const HARDCODED_SUPPLIERS: Array<{ name: string, image: App.Image }> = [
  { name: 'Avis', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/avis.svg' } },
  { name: 'Hertz', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/hertz.svg' } },
  { name: 'Europcar', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/europcar.svg' } },
  { name: 'Sixt', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/sixt.svg' } },
  { name: 'Thrifty', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/thrifty.svg' } },
  { name: 'Budget', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/budget.svg' } },
  { name: 'Alamo', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/alamo.svg' } },
  { name: 'Enterprise', image: { url: 'https://ctimg-svg.cartrawler.com/supplier-images/enterprise.svg' } },
]

export const TRANSPORTATION_CODE = {
  AIRPLANE: 14,
}

export function getCarHireFAQ(isCarHireInsuranceEnabled: boolean, intl: IntlShape): Array<FAQ> {
  const FAQ = [
    {
      icon: <LineFileAltIcon />,
      question: intl.formatMessage({
        id: '5EX1kmeWc-j47EQKBcyh',
        defaultMessage: 'What documents do I need to rent a car?',
        description: 'FAQ question about documents needed to rent a car in the car hire section',
      }),
      answer: intl.formatMessage({
        id: 'XyoUZFHfFl2HX_GDUxrt',
        defaultMessage: 'To rent a car, you will typically require:<ol><li><strong>Valid driver’s licence:</strong> Remember to carry it with you when picking up the rental car.</li><li><strong>Identification:</strong> A valid passport or national identification card to confirm your identity.</li><li><strong>Payment method:</strong> In your name with sufficient funds to cover the rental charges and any security deposit.</li></ol>',
        description: 'FAQ answer about documents needed to rent a car in the car hire section',
      }, {
        ol: chunks => `<ol>${chunks}</ol>`,
        li: chunks => `<li>${chunks}</li>`,
        strong: chunks => `<strong>${chunks}</strong>`,
      }),
    },
    {
      icon: <LineUserIcon />,
      question: intl.formatMessage({
        id: '9j547swkuZedkoLap8o6',
        defaultMessage: 'How old do I need to be to rent a car?',
        description: 'FAQ question about minimum age to rent a car in the car hire section',
      }),
      answer: intl.formatMessage({
        id: '16EOX5tD4lAuVhQprNum',
        defaultMessage: 'The minimum driver age for most rental companies is 25 years. Driver’s younger than 30 may incur a surcharge or be limited in the vehicle type they can select. Older drivers aged over 70 may require a medical and incur a surcharge. Make sure you enter your correct age, at the time of hire, when searching for a car so you can see the options available for you. All guests intending to drive must present a valid full national driving license, which has been held for at least three years. A current international driving license or translation must accompany driving licenses issued in a language other than English.',
        description: 'FAQ answer about minimum age requirements to rent a car',
      }),
    },
    {
      icon: <LineCarIcon />,
      question: intl.formatMessage({
        id: 'h5JMZH0heAOR2CiWYf79',
        defaultMessage: 'What should I look for when choosing a rental car?',
        description: 'FAQ question about choosing a rental car on the rental page',
      }),
      answer: intl.formatMessage({
        id: 'Hsf1PWj4OiizHGQ_PUbZ',
        defaultMessage: '- Passenger and baggage capacity: Ensure ample space for you, your passengers, and all your luggage by checking the passenger and baggage capacity. - Transmission: Choose between manual and automatic transmission to ensure you’re comfortable with the car’s driving system. - Mileage policy: Verify the mileage policy to understand if there are any limitations on the number of kilometers you can drive during the rental period. - Pick up and drop off location: Confirm the pick-up and drop-off location, especially for airport rentals, as you may need to utilize a free shuttle or terminal service. Find all this information in the offer and your booking confirmation.',
        description: 'FAQ answer about what to consider when choosing a rental car',
      }),
    },
    ...(isCarHireInsuranceEnabled ? [{
      icon: <LineShieldCheckIcon />,
      question: intl.formatMessage({
        id: 'tbwVO2MAFtB6tMdR1n8u',
        defaultMessage: 'Do I need insurance when renting a car?',
        description: 'FAQ question about car hire insurance in the insurance or protection selection',
      }),
      answer: intl.formatMessage({
        id: 'cCFhWj7o8bpTFA-BsDpV',
        defaultMessage: 'It is highly recommended to have insurance coverage when renting a car. Most rental companies offer insurance options, including Collision Damage Waiver (CDW) and Theft Protection, which provide financial protection in case of an accident, theft, or damage to the rental vehicle. It’s important to review the insurance terms and conditions prior to booking. Luxury Escapes offers an Enhanced Protection insurance option that covers up to 100% of the excess in case of an incident. This provides you with added peace of mind during your rental period.',
        description: 'FAQ answer about the importance of car hire insurance',
      }),
    }] : []),
    ...(isCarHireInsuranceEnabled ? [{
      icon: <LineShieldPlusIcon />,
      question: intl.formatMessage({
        id: 'm91LhZLE5Sax9kVM2Ops',
        defaultMessage: 'Can I purchase insurance through Luxury Escapes?',
        description: 'FAQ question about purchasing insurance through Luxury Escapes',
      }),
      answer: intl.formatMessage({
        id: 'WGU5A82i3J7Rhb869OC-',
        defaultMessage: 'Absolutely! Luxury Escapes offers an Enhanced Protection insurance option that covers up to 100% refundable excess in case of an incident. This provides you with added peace of mind during your rental period. Currently, Enhanced Protection coverage can be purchased as an add-on during your Car Hire checkout. Full coverage information can be found by clicking learn more on each policy on the checkout page.',
        description: 'FAQ answer about purchasing Enhanced Protection insurance through Luxury Escapes',
      }),
    }] : []),
    {
      icon: <LineExclamationTriangleIcon />,
      question: intl.formatMessage({
        id: '5lWy8y2toPX81ULepOWJ',
        defaultMessage: 'What should I do if I get into an accident in a rental car?',
        description: 'FAQ question about what to do in a rental car accident',
      }),
      answer: intl.formatMessage({
        id: '-0OHtTdsT5ET7di7weqZ',
        defaultMessage: 'If you find yourself in an accident while driving a rental car, follow these steps: 1. Ensure safety: Prioritise your safety and the safety of others involved. Move to a safe location if possible and contact emergency services if necessary. 2. Inform the rental company and police: Report the accident immediately using their designated emergency contact number. They will guide you on the necessary next steps. 3. Document the incident: Take photos of the accident scene, gather information from any involved parties or witnesses, and make note of any relevant details. This documentation may be required for insurance claims. Information you’ll likely need to collect includes registration and license number of both vehicles and drivers, address of the other driver and photos of the incident.',
        description: 'FAQ answer about steps to take after a rental car accident',
      }),
    },
    {
      icon: <LineClockEightIcon />,
      question: intl.formatMessage({
        id: 'YOQ9gapXZaPHVkKLYdeA',
        defaultMessage: 'What happens if I return the car late?',
        description: 'FAQ question about late return of rental car on the rental page',
      }),
      answer: intl.formatMessage({
        id: 'a94kTJCmMPLQ-mp__MZd',
        defaultMessage: 'Returning the rental car late may result in additional charges. Rental companies usually have specific policies regarding late returns, including fees charged per hour or additional full-day charges. It’s important to adhere to the agreed-upon return time to avoid any unexpected costs. If you anticipate being late, contact the rental company in advance to discuss possible solutions.',
        description: 'FAQ answer about consequences and advice for late car returns',
      }),
    },
    {
      icon: <LineFileTimesAltIcon />,
      question: intl.formatMessage({
        id: 'gGWMQC9K0Ome5p_NmZvi',
        defaultMessage: 'Can I cancel my rental reservation?',
        description: 'FAQ question about cancelling a rental car reservation',
      }),
      answer: intl.formatMessage({
        id: 'kfnAma9fgdxSYke0Jm1R',
        defaultMessage: 'Most reservations can be cancelled up to 48 hours prior to pick up. Your cancellation policy is clearly highlighted on your booking. Prior to purchase, review the terms and conditions of your specific reservation as they may vary depending on the rental company. To cancel your reservation, please contact our Customer Support team on {phoneNumber}.',
        description: 'FAQ answer about rental car cancellation policy in the car hire section',
      }, { phoneNumber: SUPPORT_DEFAULT_PHONE_DISPLAY }),
    },
  ]

  return FAQ
}

export function getCarHireInsuranceCovers(intl: IntlShape) {
  return [
    {
      value: intl.formatMessage({
        id: 'SRaYZ3Rke6DetsqxjrRl',
        defaultMessage: 'Non-refundable excess',
        description: 'Car hire insurance cover item: non-refundable excess',
      }),
      includedInLimitedInsurance: true,
    },
    {
      value: intl.formatMessage({
        id: 'oZAFhQT2NDWJTIOv5f8v',
        defaultMessage: 'Third Party Protection',
        description: 'Car hire insurance cover item: third party protection',
      }),
      includedInLimitedInsurance: true,
    },
    {
      value: intl.formatMessage({
        id: '_PZparOoN8i5LpWSMkm5',
        defaultMessage: 'Your personal belongings',
        description: 'Car hire insurance cover item: personal belongings',
      }),
      includedInLimitedInsurance: false,
    },
    {
      value: intl.formatMessage({
        id: 'wyq82RQz76k5mo34tBBF',
        defaultMessage: 'Overseas medical expenses',
        description: 'Car hire insurance cover item: overseas medical expenses',
      }),
      includedInLimitedInsurance: false,
    },
    {
      value: intl.formatMessage({
        id: '4sClNjC_JPpB8tp2Il76',
        defaultMessage: 'Replacement keys and locks',
        description: 'Car hire insurance cover item: replacement keys and locks',
      }),
      includedInLimitedInsurance: false,
    },
    {
      value: intl.formatMessage({
        id: '5FSVDrB9oE5oRTu7giHo',
        defaultMessage: 'Accidental death',
        description: 'Car hire insurance cover item: accidental death in the car hire section',
      }),
      includedInLimitedInsurance: false,
    },
    {
      value: intl.formatMessage({
        id: 'CVzXkiEpRKBlas1zrqT_',
        defaultMessage: 'Misfuelling fees',
        description: 'Car hire insurance cover item: misfuelling fees in the car hire section',
      }),
      includedInLimitedInsurance: false,
    },
  ]
}

function formatTrendingDestinationText(intl: IntlShape, text: string): string {
  switch (text) {
    case 'Sydney':
      return intl.formatMessage({
        id: 'wNJ8dvvJayynWY-zZZ2Q',
        defaultMessage: 'Sydney',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Hobart':
      return intl.formatMessage({
        id: 'YOIAxqG4EzML8J-nKZAu',
        defaultMessage: 'Hobart',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Waikiki':
      return intl.formatMessage({
        id: '-4cGh7Wwifdl2FkqbxjL',
        defaultMessage: 'Waikiki',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Christchurch':
      return intl.formatMessage({
        id: 'l5LmM0Tzn7-lgkei1kkH',
        defaultMessage: 'Christchurch',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Athens':
      return intl.formatMessage({
        id: '2NkNfq3nQ3Szs0jWXyBF',
        defaultMessage: 'Athens',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Rome':
      return intl.formatMessage({
        id: '6YSfS7OBlNHfsz3TfJEp',
        defaultMessage: 'Rome',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Barcelona':
      return intl.formatMessage({
        id: 'jiAAL-0icbbyj_EGGD4p',
        defaultMessage: 'Barcelona',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'London':
      return intl.formatMessage({
        id: 'WpRXlF9gY1-4ithDnhKp',
        defaultMessage: 'London',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Melbourne':
      return intl.formatMessage({
        id: 'QytQyZps0PedXrktOTim',
        defaultMessage: 'Melbourne',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Gold Coast':
      return intl.formatMessage({
        id: '3AzlFcyYlpYQtZNur1ET',
        defaultMessage: 'Gold Coast',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Cairns':
      return intl.formatMessage({
        id: '_yThcVdNJyhUHgwaiEUv',
        defaultMessage: 'Cairns',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Launceston':
      return intl.formatMessage({
        id: '77HcSRHpdIFMFNw-A_5S',
        defaultMessage: 'Launceston',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Perth':
      return intl.formatMessage({
        id: 'QwYbqjvC6obdsL0Qd-ic',
        defaultMessage: 'Perth',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Brisbane':
      return intl.formatMessage({
        id: 'ok2AguLNUFUFZfMxQ0gu',
        defaultMessage: 'Brisbane',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Auckland':
      return intl.formatMessage({
        id: 'mnFZBO5tK7OSvkgyfCSB',
        defaultMessage: 'Auckland',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'New York':
      return intl.formatMessage({
        id: 'AkcYfds6mdIZhFB4GH_c',
        defaultMessage: 'New York',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'San Francisco':
      return intl.formatMessage({
        id: 'K0Mncva6SWGGSsHIExGp',
        defaultMessage: 'San Francisco',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Adelaide':
      return intl.formatMessage({
        id: 'zUwzAFK981WFs1AoQBJM',
        defaultMessage: 'Adelaide',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'Broome':
      return intl.formatMessage({
        id: 'kKUI98sYNZaxix77_4k0',
        defaultMessage: 'Broome',
        description: 'Trending car hire destination city name shown in destination cards and the selected search location summary',
      })
    case 'New South Wales, Australia':
      return intl.formatMessage({
        id: '-wvdJfdFBFRW2usLR-6K',
        defaultMessage: 'New South Wales, Australia',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Tasmania, Australia':
      return intl.formatMessage({
        id: 'Ss48p4oMpYQbRUUvldlL',
        defaultMessage: 'Tasmania, Australia',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Honolulu, Hawaii, United States of America':
      return intl.formatMessage({
        id: 'u5J8aDHkBnUiCjC1C0gE',
        defaultMessage: 'Honolulu, Hawaii, United States of America',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Canterbury, New Zealand':
      return intl.formatMessage({
        id: '-WKl5I9Z4Wo_mFw6rrVW',
        defaultMessage: 'Canterbury, New Zealand',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Attica, Greece':
      return intl.formatMessage({
        id: 'ANS0B2wiqPVG38h3Hl47',
        defaultMessage: 'Attica, Greece',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Lazio, Italy':
      return intl.formatMessage({
        id: 'GgDI8BD0HGELbXKrOujm',
        defaultMessage: 'Lazio, Italy',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Catalonia, Spain':
      return intl.formatMessage({
        id: 'vgsLImj1L2pPaKvC-mTU',
        defaultMessage: 'Catalonia, Spain',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'England, United Kingdom':
      return intl.formatMessage({
        id: 'zg4fcVhdBOIrM-pdgMC4',
        defaultMessage: 'England, United Kingdom',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Victoria, Australia':
      return intl.formatMessage({
        id: 'ngSD99R2sQKjAFEgU3t3',
        defaultMessage: 'Victoria, Australia',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Queensland, Australia':
      return intl.formatMessage({
        id: 'H1SHnMYCfTy76d134S33',
        defaultMessage: 'Queensland, Australia',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Cairns, Queensland, Australia':
      return intl.formatMessage({
        id: '-cK6aH3yWClOXCrhBPMm',
        defaultMessage: 'Cairns, Queensland, Australia',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Western Australia, Australia':
      return intl.formatMessage({
        id: 'LChGYn0q0sZ7P5Jbv1eY',
        defaultMessage: 'Western Australia, Australia',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'Auckland Region, New Zealand':
      return intl.formatMessage({
        id: '24PUsF9PzKU4x5bUnd0L',
        defaultMessage: 'Auckland Region, New Zealand',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'New York, United States of America':
      return intl.formatMessage({
        id: 'QEi14jGFOQv1ikr460jO',
        defaultMessage: 'New York, United States of America',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'California, United States of America':
      return intl.formatMessage({
        id: '31PWcANYFiTTrHrqRXNW',
        defaultMessage: 'California, United States of America',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    case 'South Australia, Australia':
      return intl.formatMessage({
        id: '7irjyGtwvqLsnN4Pqm6k',
        defaultMessage: 'South Australia, Australia',
        description: 'Trending car hire destination subtitle shown in the selected search location and related car hire browsing UI',
      })
    default:
      return text
  }
}

export function getTrendingDestinationToSearchLocation(intl: IntlShape): Map<string, App.SearchItem> {
  return new Map<string, App.SearchItem>([
    ['SYDNEY', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_678c61ac5b4f91ca86e112fc50e63766',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Sydney'),
        secondaryText: formatTrendingDestinationText(intl, 'New South Wales, Australia'),
      },
    }],
    ['HOBART', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_b573c52d599a979ba8c16e9789bb8014',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Hobart'),
        secondaryText: formatTrendingDestinationText(intl, 'Tasmania, Australia'),
      },
    }],
    ['WAIKIKI', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_21bf8d519b1e67cd0b9d4b50b55fccdd',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Waikiki'),
        secondaryText: formatTrendingDestinationText(intl, 'Honolulu, Hawaii, United States of America'),
      },
    }],
    ['CHRISTCHURCH', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_1e15efc3fac4ba31daa6f91082c97e97',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Christchurch'),
        secondaryText: formatTrendingDestinationText(intl, 'Canterbury, New Zealand'),
      },
    }],
    ['ATHENS', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_342fdc93f26b8bb8fe3548a9188ea5d2',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Athens'),
        secondaryText: formatTrendingDestinationText(intl, 'Attica, Greece'),
      },
    }],
    ['ROME', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_832d851fd4d8851affa0b77077f5b4d6',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Rome'),
        secondaryText: formatTrendingDestinationText(intl, 'Lazio, Italy'),
      },
    }],
    ['BARCELONA', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_af15ce7cbd279b1a769d85d1540a5073',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Barcelona'),
        secondaryText: formatTrendingDestinationText(intl, 'Catalonia, Spain'),
      },
    }],
    ['LONDON', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_66ff0e7c30438d414ff0a941b759f114',
      format: {
        mainText: formatTrendingDestinationText(intl, 'London'),
        secondaryText: formatTrendingDestinationText(intl, 'England, United Kingdom'),
      },
    }],
    ['MELBOURNE', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_15fa44474c92da27cf2125373356db4c',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Melbourne'),
        secondaryText: formatTrendingDestinationText(intl, 'Victoria, Australia'),
      },
    }],
    ['GOLD COAST', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_bac732346917b0a1231354ef55faf00a',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Gold Coast'),
        secondaryText: formatTrendingDestinationText(intl, 'Queensland, Australia'),
      },
    }],
    ['CAIRNS', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_55dddb74a8c2e96db33e4363b0a73b98',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Cairns'),
        secondaryText: formatTrendingDestinationText(intl, 'Cairns, Queensland, Australia'),
      },
    }],
    ['LAUNCESTON', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_019cf615e71dc6ec778f64fb0022f3d9',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Launceston'),
        secondaryText: formatTrendingDestinationText(intl, 'Tasmania, Australia'),
      },
    }],
    ['PERTH', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_7c27cf9f8fe09724bb754e474610eb0e',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Perth'),
        secondaryText: formatTrendingDestinationText(intl, 'Western Australia, Australia'),
      },
    }],
    ['BRISBANE', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_b96d145f6c31e35755f4a6841574ba37',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Brisbane'),
        secondaryText: formatTrendingDestinationText(intl, 'Queensland, Australia'),
      },
    }],
    ['AUCKLAND', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_464adf7b911508701f4c4e70eab19a6e',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Auckland'),
        secondaryText: formatTrendingDestinationText(intl, 'Auckland Region, New Zealand'),
      },
    }],
    ['NEW YORK', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_1ada7faf229425b786d87f7d9d5d43f2',
      format: {
        mainText: formatTrendingDestinationText(intl, 'New York'),
        secondaryText: formatTrendingDestinationText(intl, 'New York, United States of America'),
      },
    }],
    ['SAN FRANCISCO', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_bd80d53a4b5d3aef9d8a1fabe2feecfc',
      format: {
        mainText: formatTrendingDestinationText(intl, 'San Francisco'),
        secondaryText: formatTrendingDestinationText(intl, 'California, United States of America'),
      },
    }],
    ['ADELAIDE', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_f4c2c73d7fe5eb38fd83d32a61604247',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Adelaide'),
        secondaryText: formatTrendingDestinationText(intl, 'South Australia, Australia'),
      },
    }],
    ['BROOME', {
      searchType: 'destination',
      destinationType: 'city',
      value: 'le_83fba92646fd4b0c7d8995bf405214c8',
      format: {
        mainText: formatTrendingDestinationText(intl, 'Broome'),
        secondaryText: formatTrendingDestinationText(intl, 'Western Australia, Australia'),
      },
    }],
  ])
}

export const trendingDestinationsToImageId = new Map<string, string>([
  ['SYDNEY', 'sl51ijurwy2yd2grv63'],
  ['WAIKIKI', 'sl51ijurwy2yd2grv63'],
  ['CHRISTCHURCH', 'er18h25ch9z4igigtj'],
  ['ATHENS', '1fem2o5idwichvob7lh'],
  ['ROME', 'awrbmy83k9yawi7pre'],
  ['BARCELONA', '4rtbj79ohskfqb6iwa'],
  ['LONDON', '8j2786i5gb1lwqp27bkj'],
  ['MELBOURNE', 'asgz0fkt6cf311m98rq'],
  ['GOLD COAST', 'l85etp6jx8l4q5e9066e'],
  ['CAIRNS', 'o7kc5i36pgxedgdlkg0g'],
  ['HOBART', '1p3vk99nrobag2fhdzsx'],
  ['LAUNCESTON', '1qtj8zwzzoq8dtb1em2w'],
  ['PERTH', 'e9zytyo6qkuq0xsiwra'],
  ['BRISBANE', 'fnd6pvqqrne0iq6jrqs'],
  ['AUCKLAND', 'pzjxqagnbik2lesq86x'],
  ['NEW YORK', 'qqh34vxmsflghdk6q9f'],
  ['SAN FRANCISCO', '2pzhx667zpbolv1dup'],
  ['ADELAIDE', 'idk3in3pu8uhpknzafe'],
  ['BROOME', 'diroqvfo7ghqesx0dam'],
])

export const TRENDING_DESTINATIONS: Record<string, Pick<App.CarHireDestinationPriceInfoParams, 'cities' | 'airports'>> = {
  WORLDWIDE: {
    cities: ['Waikiki', 'Christchurch', 'Athens', 'New York', 'San Francisco', 'Rome', 'Barcelona', 'London', 'Hobart', 'Auckland'],
  },
  AUSTRALIA: {
    cities: ['Sydney', 'Melbourne', 'Gold coast', 'Cairns', 'Adelaide', 'Hobart', 'Launceston', 'Broome', 'Brisbane'],
  },
}
